import React, { useState, useEffect } from 'react';
import { useGetSubscriptionQuery } from '../../redux/slices/subscription/subscriptionApi';
import { Header, ExportCSVButton } from '../../components';
import SearchBox from '../../components/SearchBox';
import { useStateContext } from '../../contexts/ContextProvider';
import ComingSoonPopup from '../../components/ComingSoonPopup';

const SubscriptionPage = () => {
  const { data, isLoading, error } = useGetSubscriptionQuery();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('start_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showComingSoon, setShowComingSoon] = useState(false);
  const recordsPerPage = 10;
  const { currentMode } = useStateContext();

  
  useEffect(() => {
    setShowComingSoon(true);
  }, []);

  if (isLoading) {
    return (
      <div className={`m-2 md:m-10 mt-24 p-2 md:p-10 rounded-3xl ${currentMode === 'Dark' ? 'bg-secondary-dark-bg' : 'bg-white'}`}>
        <Header category="Page" title="Subscriptions" />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`m-2 md:m-10 mt-24 p-2 md:p-10 rounded-3xl ${currentMode === 'Dark' ? 'bg-secondary-dark-bg' : 'bg-white'}`}>
        <Header category="Page" title="Subscriptions" />
        <div className="flex items-center justify-center py-16">
          <div className={`text-center rounded-lg p-8 max-w-md mx-auto border ${currentMode === 'Dark' ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <div className="mb-4">
              <svg 
                className="w-16 h-16 mx-auto text-red-500" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={1.5} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" 
                />
              </svg>
            </div>
            <h3 className={`text-lg font-semibold mb-2 ${currentMode === 'Dark' ? 'text-white' : 'text-gray-700'}`}>Unable to Load Subscriptions</h3>
            <p className={`text-sm mb-4 ${currentMode === 'Dark' ? 'text-gray-300' : 'text-gray-500'}`}>
              We encountered an issue while loading the subscription data. Please try refreshing the page or contact support if the problem persists.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  const subscriptions = data?.data || [];
  
  // Filter by email
  const filtered = subscriptions.filter((item) => item.User?.email?.toLowerCase().includes(searchTerm.toLowerCase()));

  // Sort function
  const sortRecords = (dataRecords, field, direction) => [...dataRecords].sort((a, b) => {
    let aValue = a[field];
    let bValue = b[field];

    // Handle date fields
    if (field === 'start_date' || field === 'end_date') {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
    }

    // Handle nested user fields
    if (field === 'user_email') {
      aValue = a.User?.email || '';
      bValue = b.User?.email || '';
    }

    if (field === 'user_name') {
      aValue = `${a.User?.first_name || ''} ${a.User?.last_name || ''}`.trim().toLowerCase();
      bValue = `${b.User?.first_name || ''} ${b.User?.last_name || ''}`.trim().toLowerCase();
    }

    if (field === 'user_id') {
      aValue = a.User?.id || '';
      bValue = b.User?.id || '';
    }

    if (field === 'subscription_name') {
      aValue = a.Subscriptions?.name || '';
      bValue = b.Subscriptions?.name || '';
    }

    // Handle string fields
    if (typeof aValue === 'string' && !['user_name', 'user_email', 'user_id', 'subscription_name'].includes(field)) {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';

    if (direction === 'asc') {
      if (aValue > bValue) return 1;
      if (aValue < bValue) return -1;
      return 0;
    }
    if (aValue < bValue) return 1;
    if (aValue > bValue) return -1;
    return 0;
  });

  // Apply sorting to filtered records
  const sortedRecords = sortRecords(filtered, sortField, sortDirection);

  // Handle sort column click
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Get sort indicator
  const getSortIndicator = (field) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 ml-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 ml-1 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Pagination
  const indexOfLast = currentPage * recordsPerPage;
  const indexOfFirst = indexOfLast - recordsPerPage;
  const currentRecords = sortedRecords.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedRecords.length / recordsPerPage);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPlanName = (plan) => {
    if (!plan) return '';
    return (
      <span className="bg-green-100 text-green-800 font-bold rounded-full px-3 py-1 inline-block">
        {plan}
      </span>
    );
  };

  const getRowBackgroundClass = (index) => {
    if (currentMode === 'Dark') {
      return index % 2 === 0 ? 'bg-secondary-dark-bg' : 'bg-secondary-dark-bg-light';
    }
    return index % 2 === 0 ? 'bg-white' : 'bg-gray-50';
  };

  return (
    <div className={`m-2 md:m-10 mt-24 p-2 md:p-10 rounded-3xl ${currentMode === 'Dark' ? 'bg-secondary-dark-bg' : 'bg-white shadow-lg'}`}>
      <Header category="Page" title="Subscriptions" />
      {/* Search Box and Export Button */}
      <div className="mb-4 flex items-center justify-between">
        <SearchBox
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by email..."
        />
        <ExportCSVButton
          data={sortedRecords}
          filename="subscriptions.xlsx"
          buttonText="Export to Excel"
        />
      </div>
      {/* Records Count */}
      <div className="mb-4">
        <p className={currentMode === 'Dark' ? 'text-white' : 'text-gray-600'}>
          Showing {sortedRecords.length === 0 ? 0 : indexOfFirst + 1} to {Math.min(indexOfLast, sortedRecords.length)} of {sortedRecords.length} records
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className={`min-w-max border ${currentMode === 'Dark' ? 'bg-secondary-dark-bg border-gray-600' : 'bg-white border-gray-300'}`}>
          <thead className={currentMode === 'Dark' ? 'bg-secondary-dark-bg-light' : 'bg-gray-50'}>
            <tr>
              <th 
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${currentMode === 'Dark' ? 'text-white border-gray-600 hover:bg-gray-700' : 'text-gray-500 border-gray-300'}`}
                onClick={() => handleSort('user_id')}
              >
                <div className="flex items-center">
                  User Id
                  {getSortIndicator('user_id')}
                </div>
              </th>
              <th 
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${currentMode === 'Dark' ? 'text-white border-gray-600 hover:bg-gray-700' : 'text-gray-500 border-gray-300'}`}
                onClick={() => handleSort('user_name')}
              >
                <div className="flex items-center">
                  Name
                  {getSortIndicator('user_name')}
                </div>
              </th>
              <th 
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${currentMode === 'Dark' ? 'text-white border-gray-600 hover:bg-gray-700' : 'text-gray-500 border-gray-300'}`}
                onClick={() => handleSort('user_email')}
              >
                <div className="flex items-center">
                  Email
                  {getSortIndicator('user_email')}
                </div>
              </th>
              <th 
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${currentMode === 'Dark' ? 'text-white border-gray-600 hover:bg-gray-700' : 'text-gray-500 border-gray-300'}`}
                onClick={() => handleSort('subscription_name')}
              >
                <div className="flex items-center">
                  Subscription
                  {getSortIndicator('subscription_name')}
                </div>
              </th>
              <th 
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${currentMode === 'Dark' ? 'text-white border-gray-600 hover:bg-gray-700' : 'text-gray-500 border-gray-300'}`}
                onClick={() => handleSort('subscriptionsId')}
              >
                <div className="flex items-center">
                  Subscription Id
                  {getSortIndicator('subscriptionsId')}
                </div>
              </th>
              <th 
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${currentMode === 'Dark' ? 'text-white border-gray-600 hover:bg-gray-700' : 'text-gray-500 border-gray-300'}`}
                onClick={() => handleSort('used_aum')}
              >
                <div className="flex items-center">
                  Used AUM
                  {getSortIndicator('used_aum')}
                </div>
              </th>
              <th 
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${currentMode === 'Dark' ? 'text-white border-gray-600 hover:bg-gray-700' : 'text-gray-500 border-gray-300'}`}
                onClick={() => handleSort('start_date')}
              >
                <div className="flex items-center">
                  Start Date
                  {getSortIndicator('start_date')}
                </div>
              </th>
              <th 
                className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider border-b cursor-pointer hover:bg-gray-100 ${currentMode === 'Dark' ? 'text-white border-gray-600 hover:bg-gray-700' : 'text-gray-500 border-gray-300'}`}
                onClick={() => handleSort('end_date')}
              >
                <div className="flex items-center">
                  End Date
                  {getSortIndicator('end_date')}
                </div>
              </th>
            </tr>
          </thead>
          <tbody className={`divide-y ${currentMode === 'Dark' ? 'bg-secondary-dark-bg divide-gray-600' : 'bg-white divide-gray-200'}`}>
            {currentRecords.map((item, index) => (
              <tr key={item.id} className={getRowBackgroundClass(index)}>
                <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${currentMode === 'Dark' ? 'text-white' : 'text-gray-900'}`}>{item.User?.id}</td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${currentMode === 'Dark' ? 'text-white' : 'text-gray-500'}`}>{item.User?.first_name} {item.User?.last_name}</td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${currentMode === 'Dark' ? 'text-white' : 'text-gray-500'}`}>{item.User?.email}</td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${currentMode === 'Dark' ? 'text-white' : 'text-gray-500'}`}>{renderPlanName(item.Subscriptions?.name)}</td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${currentMode === 'Dark' ? 'text-white' : 'text-gray-500'}`}>{item.subscriptionsId}</td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${currentMode === 'Dark' ? 'text-white' : 'text-gray-500'}`}>{item.used_aum}</td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${currentMode === 'Dark' ? 'text-white' : 'text-gray-500'}`}>{item.start_date ? new Date(item.start_date).toLocaleDateString() : ''}</td>
                <td className={`px-6 py-4 whitespace-nowrap text-sm ${currentMode === 'Dark' ? 'text-white' : 'text-gray-500'}`}>{item.end_date ? new Date(item.end_date).toLocaleDateString() : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {/* Page Numbers */}
            <div className="flex space-x-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === page ? 'bg-blue-600 text-white' : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'}`}
                >
                  {page}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      )}
      {/* No Records Message */}
      {sortedRecords.length === 0 && (
        <div className="flex items-center justify-center py-16">
          <div className={`text-center rounded-lg p-8 max-w-md mx-auto border ${currentMode === 'Dark' ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <div className="mb-4">
              <svg 
                className={`w-16 h-16 mx-auto ${currentMode === 'Dark' ? 'text-gray-500' : 'text-gray-400'}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={1.5} 
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" 
                />
              </svg>
            </div>
            <h3 className={`text-lg font-semibold mb-2 ${currentMode === 'Dark' ? 'text-white' : 'text-gray-700'}`}>No Subscriptions Found</h3>
            <p className={`text-sm ${currentMode === 'Dark' ? 'text-gray-300' : 'text-gray-500'}`}>
              {searchTerm ? 'No subscriptions match your search criteria. Try adjusting your search terms.' : 'There are currently no subscriptions available.'}
            </p>
          </div>
        </div>
      )}
      
      <ComingSoonPopup 
        isOpen={showComingSoon} 
        onClose={() => setShowComingSoon(false)} 
      />
    </div>
  );
};

export default SubscriptionPage;
